import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Mail, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { formatApiError } from '@/lib/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_inventory-engine-2/artifacts/2d9zezs2_Zippy%20Logo%202.PNG';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg('');
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setDone(true);
    } catch (err) {
      setErrorMsg(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center mb-6">
          <img src={LOGO_URL} alt="Zippy WMS" className="h-14 w-auto" />
        </div>
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Forgot your password?</CardTitle>
            <CardDescription>
              Enter the email tied to your account and we&rsquo;ll send you a link to set a new password.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {done ? (
              <div className="space-y-4" data-testid="forgot-password-success">
                <div className="flex items-start gap-3 rounded-lg bg-green-50 border border-green-200 p-4">
                  <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-slate-800">
                    <p className="font-medium">Check your inbox</p>
                    <p className="text-slate-600 mt-1">
                      If an account exists for <span className="font-mono">{email}</span>, a reset link has been sent.
                      The link expires in 60 minutes.
                    </p>
                  </div>
                </div>
                <Button asChild variant="outline" className="w-full">
                  <Link to="/login" data-testid="back-to-login">
                    <ArrowLeft className="h-4 w-4 mr-2" />Back to sign in
                  </Link>
                </Button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="forgot-email">Email</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    autoFocus
                    data-testid="forgot-email-input"
                  />
                </div>
                {errorMsg && (
                  <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                    {errorMsg}
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#1680e5] hover:bg-[#1680e5]/90"
                  data-testid="forgot-submit-button"
                >
                  {loading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending…</>
                  ) : (
                    <><Mail className="h-4 w-4 mr-2" />Send reset link</>
                  )}
                </Button>
                <Button asChild variant="ghost" className="w-full" size="sm">
                  <Link to="/login" data-testid="forgot-back-to-login">
                    <ArrowLeft className="h-4 w-4 mr-2" />Back to sign in
                  </Link>
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
